/**
 * KeyValueEditor — 键值对变量编辑器（用于试发/调用时传模板变量）
 * 每行：变量名 + 变量值，避免手写 JSON。输出 JSON 对象 {变量名: 值}。
 */
import { useEffect, useState } from 'react'
import { makeStyles, tokens, Input, Button, Text } from '@fluentui/react-components'
import { AddRegular, DeleteRegular } from '@fluentui/react-icons'

interface Props {
  value: Record<string, string>
  onChange: (v: Record<string, string>) => void
}

interface Row {
  key: string
  value: string
}

const useStyles = makeStyles({
  wrap: { display: 'flex', flexDirection: 'column', gap: '8px' },
  row: { display: 'flex', gap: '8px', alignItems: 'center' },
  keyInput: { flex: '0 0 200px', minWidth: '140px' },
  valueInput: { flex: 1, minWidth: '160px' },
  empty: { fontSize: tokens.fontSizeBase100, color: tokens.colorNeutralForeground3 },
  toolbar: { display: 'flex', gap: '8px', alignItems: 'center' },
})

const toRows = (v: Record<string, string>): Row[] =>
  Object.entries(v ?? {}).map(([key, value]) => ({ key, value: String(value) }))

const toRecord = (rows: Row[]): Record<string, string> =>
  rows.reduce<Record<string, string>>((acc, r) => {
    if (r.key.trim()) acc[r.key.trim()] = r.value
    return acc
  }, {})

export default function KeyValueEditor({ value, onChange }: Props) {
  const styles = useStyles()
  const [rows, setRows] = useState<Row[]>(() => toRows(value))

  // 仅当外部 value 与当前已编辑内容不一致时才同步，避免编辑输入产生回环
  useEffect(() => {
    setRows((prev) => (JSON.stringify(toRecord(prev)) === JSON.stringify(value) ? prev : toRows(value)))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(value)])

  const addRow = () => setRows((r) => [...r, { key: '', value: '' }])
  const updateRow = (i: number, patch: Partial<Row>) =>
    setRows((prev) => {
      const next = prev.map((r, j) => (j === i ? { ...r, ...patch } : r))
      onChange(toRecord(next))
      return next
    })
  const removeRow = (i: number) =>
    setRows((prev) => {
      const next = prev.filter((_, j) => j !== i)
      onChange(toRecord(next))
      return next
    })

  return (
    <div className={styles.wrap}>
      {rows.length === 0 && (
        <Text className={styles.empty}>（点击「添加变量」，每行填一个变量名和值，无需手写 JSON）</Text>
      )}
      {rows.map((r, i) => (
        <div key={i} className={styles.row}>
          <Input
            className={styles.keyInput}
            placeholder="变量名，如 code"
            value={r.key}
            onChange={(_, d) => updateRow(i, { key: d.value })}
          />
          <Input
            className={styles.valueInput}
            placeholder="变量值"
            value={r.value}
            onChange={(_, d) => updateRow(i, { value: d.value })}
          />
          <Button appearance="subtle" size="small" icon={<DeleteRegular />} aria-label="删除变量" onClick={() => removeRow(i)} />
        </div>
      ))}
      <div className={styles.toolbar}>
        <Button appearance="subtle" size="small" icon={<AddRegular />} onClick={addRow}>
          添加变量
        </Button>
      </div>
    </div>
  )
}